import { useEffect, useState } from 'react';
import { GripVertical, Plus, RotateCcw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { ColorSwatchPopover } from '@/components/settings/queues/color-swatch-popover';
import { IconPickerPopover } from '@/components/settings/work-plan/icon-picker-popover';
import { fetchWorkFlags, putWorkFlags } from '@/lib/api/work-plan';
import { nextUnusedColorId } from '@/lib/thread-groups';
import {
  DEFAULT_FLAG_SUGGESTIONS,
  FLAG_DESCRIPTION_MAX,
  FLAG_ICON_FALLBACK,
  FLAG_LABEL_MAX,
  MAX_FLAGS,
  flagTone,
  normalizeFlagColorId,
  normalizeFlagDefs,
  slugFromLabel,
} from '@/lib/work-plan-flags';

// Editor for the staff-flag vocabulary (global settings, org-wide) — the flags a planner can
// attach to a posted person in a clinic×session cell ("Stay back roster", "Key holder", …).
// A clone of the work-roles editor with three more columns (icon · colour · description):
// sortable rows, ids derived from the label on first save and then FROZEN (they are stored on
// postings, so renaming keeps every posting's flag while retyping an id would orphan it),
// dirty/save/discard. Max 24 flags. An older backend has no /api/work-plan/flags (404) →
// read-only "not supported" note.

function SortableFlagRow({ row, index, canEdit, usedColorIds, onChange, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: row.key,
    disabled: !canEdit,
  });
  const tone = flagTone(row.colorId);
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        'flex flex-wrap items-center gap-2 rounded-lg border border-border bg-card px-2 py-1.5',
        isDragging && 'opacity-60 shadow-md',
      )}
    >
      {canEdit ? (
        <button
          type="button"
          className="flex size-6 shrink-0 cursor-grab items-center justify-center rounded text-muted-foreground hover:bg-muted"
          title="Drag to reorder"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="size-3.5" />
        </button>
      ) : (
        <span className="size-6 shrink-0" />
      )}
      <IconPickerPopover
        value={row.icon}
        onChange={(icon) => onChange(index, { icon })}
        disabled={!canEdit}
        label={`Flag ${index + 1} icon`}
        toneClassName={cn(tone.glyph, tone.soft)}
      />
      <ColorSwatchPopover
        colorId={row.colorId}
        onChange={(colorId) => onChange(index, { colorId })}
        usedColorIds={usedColorIds}
        disabled={!canEdit}
        label={`Flag ${index + 1} colour`}
        size="lg"
      />
      <Input
        value={row.label}
        onChange={(event) => onChange(index, { label: event.target.value })}
        placeholder="Flag name"
        disabled={!canEdit}
        maxLength={FLAG_LABEL_MAX}
        className="h-8 w-44 text-sm"
        aria-label={`Flag ${index + 1} name`}
      />
      <Input
        value={row.description}
        onChange={(event) => onChange(index, { description: event.target.value })}
        placeholder="What it means (optional)"
        disabled={!canEdit}
        maxLength={FLAG_DESCRIPTION_MAX}
        className="h-8 min-w-40 flex-1 text-sm"
        aria-label={`Flag ${index + 1} description`}
      />
      <code
        className="hidden w-28 shrink-0 truncate rounded bg-muted px-1.5 py-1 font-mono text-[10.5px] text-muted-foreground lg:block"
        title={row.id ? `Stored id: ${row.id}` : 'Id is generated from the name when you save'}
      >
        {row.id || slugFromLabel(row.label) || '—'}
      </code>
      {canEdit ? (
        <button
          type="button"
          className="flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-950/40"
          title="Remove this flag"
          onClick={() => onRemove(index)}
        >
          <Trash2 className="size-3.5" />
        </button>
      ) : null}
    </div>
  );
}

const rowsEqual = (a, b) =>
  a.id === b.id &&
  a.label.trim() === b.label &&
  a.description.trim() === b.description &&
  a.icon === b.icon &&
  a.colorId === b.colorId;

export function FlagsEditor({ canEdit = false }) {
  const [rows, setRows] = useState([]);
  const [baseline, setBaseline] = useState([]);
  const [serverCanEdit, setServerCanEdit] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [unsupported, setUnsupported] = useState(false);
  // Local keys keep dnd-kit and React stable while a brand-new row has no server id yet.
  const [keySeq, setKeySeq] = useState(0);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const toRows = (flags, startSeq) =>
    (flags || []).map((flag, i) => ({
      key: `f${startSeq + i}`,
      id: flag.id || '',
      label: flag.label || flag.id || '',
      description: flag.description || '',
      icon: flag.icon || FLAG_ICON_FALLBACK,
      colorId: normalizeFlagColorId(flag.colorId),
    }));
  const toBaseline = (flags) =>
    (flags || []).map((flag) => ({
      id: flag.id,
      label: flag.label,
      description: flag.description,
      icon: flag.icon,
      colorId: flag.colorId,
    }));

  useEffect(() => {
    let cancelled = false;
    fetchWorkFlags()
      .then((res) => {
        if (cancelled) return;
        const flags = normalizeFlagDefs(res.flags);
        setRows(toRows(flags, 0));
        setBaseline(toBaseline(flags));
        setKeySeq(flags.length);
        setServerCanEdit(Boolean(res.canEdit));
        setIsLoading(false);
      })
      .catch((error) => {
        if (cancelled) return;
        setIsLoading(false);
        if (error?.status === 404) {
          setUnsupported(true);
          return;
        }
        setLoadFailed(true);
        toast.error(error instanceof Error ? error.message : 'Could not load staff flags.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const editable = canEdit && serverCanEdit && !unsupported && !loadFailed;
  const isDirty = rows.length !== baseline.length || rows.some((row, i) => !rowsEqual(row, baseline[i]));
  const atCap = rows.length >= MAX_FLAGS;

  const addRow = (preset = {}) => {
    if (atCap) return;
    setRows((current) => [
      ...current,
      {
        key: `f${keySeq}`,
        id: '',
        label: preset.label || '',
        description: preset.description || '',
        icon: preset.icon || FLAG_ICON_FALLBACK,
        colorId: preset.colorId || nextUnusedColorId(current),
      },
    ]);
    setKeySeq((seq) => seq + 1);
  };

  const handleSave = async () => {
    if (!editable) return;
    const cleaned = rows
      .map((row) => ({
        id: row.id,
        label: row.label.trim(),
        description: row.description.trim(),
        icon: row.icon || FLAG_ICON_FALLBACK,
        colorId: row.colorId,
      }))
      .filter((row) => row.label || row.id);
    if (cleaned.some((row) => !row.label)) {
      toast.error('Every flag needs a name.');
      return;
    }
    const slugs = cleaned.map((row) => row.id || slugFromLabel(row.label));
    if (slugs.some((slug) => !slug)) {
      toast.error('Flag names must contain at least one letter or number.');
      return;
    }
    if (new Set(slugs).size !== slugs.length) {
      toast.error('Two flags resolve to the same id — give them more distinct names.');
      return;
    }
    if (cleaned.length > MAX_FLAGS) {
      toast.error(`At most ${MAX_FLAGS} flags.`);
      return;
    }
    setIsSaving(true);
    try {
      const res = await putWorkFlags({ flags: cleaned });
      const saved = normalizeFlagDefs(res.flags);
      setRows(toRows(saved, keySeq));
      setKeySeq((seq) => seq + saved.length);
      setBaseline(toBaseline(saved));
      toast.success('Staff flags saved for everyone.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not save staff flags.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setRows(toRows(baseline, keySeq));
    setKeySeq((seq) => seq + baseline.length);
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
        <Spinner className="size-4" />
        Loading staff flags…
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {unsupported ? (
        <p className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
          Staff flags are not supported by this server yet — the Work Plan shows no flag menu until the backend
          is updated.
        </p>
      ) : null}
      {loadFailed ? (
        <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
          Staff flags could not be loaded. Saving now would overwrite the stored list — reload before editing.
        </p>
      ) : null}

      <div className="flex flex-col gap-1.5">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={({ active, over }) => {
            if (!over || active.id === over.id) return;
            setRows((current) => {
              const from = current.findIndex((row) => row.key === active.id);
              const to = current.findIndex((row) => row.key === over.id);
              if (from < 0 || to < 0) return current;
              const next = [...current];
              const [moved] = next.splice(from, 1);
              next.splice(to, 0, moved);
              return next;
            });
          }}
        >
          <SortableContext items={rows.map((row) => row.key)} strategy={verticalListSortingStrategy}>
            {rows.map((row, index) => (
              <SortableFlagRow
                key={row.key}
                row={row}
                index={index}
                canEdit={editable}
                // Colours already taken by the OTHER rows (informational; reuse is allowed).
                usedColorIds={rows.filter((_, ri) => ri !== index).map((r) => r.colorId)}
                onChange={(i, patch) =>
                  setRows((current) => current.map((r, ri) => (ri === i ? { ...r, ...patch } : r)))
                }
                onRemove={(i) => setRows((current) => current.filter((_, ri) => ri !== i))}
              />
            ))}
          </SortableContext>
        </DndContext>

        {rows.length === 0 && !unsupported ? (
          <div className="rounded-lg border border-dashed border-border px-4 py-6 text-center text-xs text-muted-foreground">
            No staff flags configured. Postings carry no flags and the planner menus show no flag section.
            {editable ? (
              <span className="mt-2 flex flex-wrap justify-center gap-1.5">
                {DEFAULT_FLAG_SUGGESTIONS.map((suggestion) => (
                  <button
                    key={suggestion.label}
                    type="button"
                    className="rounded-full bg-muted px-2.5 py-1 text-[11px] font-medium text-foreground hover:bg-muted/70"
                    onClick={() => addRow(suggestion)}
                  >
                    + {suggestion.label}
                  </button>
                ))}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>

      {editable ? (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="[&_svg]:size-3.5"
            onClick={() => addRow()}
            disabled={atCap}
            title={atCap ? `At most ${MAX_FLAGS} flags` : undefined}
          >
            <Plus />
            Add flag
          </Button>
          <span className="text-[11px] text-muted-foreground">
            {rows.length}/{MAX_FLAGS}
          </span>
          <span className="flex-1" />
          {isDirty ? (
            <Button type="button" variant="ghost" size="sm" className="[&_svg]:size-3.5" onClick={handleReset} disabled={isSaving}>
              <RotateCcw />
              Discard changes
            </Button>
          ) : null}
          <Button type="button" size="sm" onClick={handleSave} disabled={!isDirty || isSaving}>
            {isSaving ? 'Saving…' : 'Save staff flags'}
          </Button>
        </div>
      ) : unsupported ? null : (
        <p className="text-xs text-muted-foreground">
          You need the scheduling <span className="font-semibold">manage</span> permission to edit these.
        </p>
      )}

      <p className="text-[11.5px] leading-relaxed text-muted-foreground">
        Planners attach flags to a posted person from the posting menu (Table) or bar menu (Timeline); the icon
        shows next to the name on the planner and on the phone. Renaming a flag keeps it on every posting — the
        stored id (shown on the right) never changes once saved. Removing a flag drops it from the menus; postings
        that still carry the old id simply stop showing it.
      </p>
    </div>
  );
}
